import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowRight,
  ArrowLeft,
  Folder,
  Palette,
  Bot,
  Sparkles,
  CheckCircle2,
  Cpu,
  MonitorSmartphone
} from 'lucide-react'

const AI_AGENTS = [
  { id: 'claude', label: 'Claude 3.5 Sonnet' },
  { id: 'chatgpt', label: 'GPT-4o' },
  { id: 'gemini', label: 'Gemini 1.5 Pro' },
]

function Progress({ step }) {
  const percent = Math.round((step / 3) * 100)
  return (
    <div className="setup-progress-wrap">
      <div className="setup-progress-copy">
        <span>Step {step} of 3</span>
        <span>{percent}% Complete</span>
      </div>
      <div className="setup-progress-track">
        <div className="setup-progress-fill" style={{ width: `${percent}%` }} />
      </div>
    </div>
  )
}

function StepWelcome({ onNext }) {
  return (
    <div className="setup-step">
      <div className="setup-icon-wrapper">
        <Sparkles size={40} className="text-primary" />
      </div>
      <h2>Welcome to Arkitect IDE</h2>
      <p>Your intelligent, AI-native development environment. Let's get things set up so you can start building faster than ever.</p>

      <div className="setup-features-grid">
        <div className="setup-feature-card">
          <Cpu size={24} />
          <div>
            <strong>AI-Native Workflows</strong>
            <span>Built from the ground up for AI-assisted coding.</span>
          </div>
        </div>
        <div className="setup-feature-card">
          <MonitorSmartphone size={24} />
          <div>
            <strong>Universal Projects</strong>
            <span>Generate Web, Mobile, and Desktop apps seamlessly.</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function StepWorkspace({ form, setForm }) {
  const handleDirectorySelect = async () => {
    try {
      if (window.arkitectDesktop?.workspace?.selectDirectory) {
        const dir = await window.arkitectDesktop.workspace.selectDirectory()
        if (dir) {
          setForm(prev => ({ ...prev, workspaceDir: dir }))
        }
      } else {
        // Fallback for dev mode without electron api
        setForm(prev => ({ ...prev, workspaceDir: '/Users/dev/ArkitectProjects' }))
      }
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <div className="setup-step">
      <div className="setup-icon-wrapper">
        <Folder size={40} className="text-primary" />
      </div>
      <h2>Configure Workspace</h2>
      <p>Where should Arkitect store your generated projects and local files?</p>

      <label className="setup-field">
        <span>Default Workspace Directory</span>
        <div className="setup-field-row">
          <input
            type="text"
            value={form.workspaceDir}
            onChange={(e) => setForm(prev => ({...prev, workspaceDir: e.target.value}))}
            placeholder="e.g. /Users/name/Projects"
            readOnly
          />
          <button type="button" className="setup-btn-secondary" onClick={handleDirectorySelect}>
            Browse...
          </button>
        </div>
      </label>

      <label className="setup-field mt-4">
        <span>Default Project Visibility</span>
        <select
          value={form.visibility}
          onChange={(e) => setForm(prev => ({...prev, visibility: e.target.value}))}
        >
          <option value="private">Private (Local only)</option>
          <option value="public">Public (Sync to Arkitect Cloud)</option>
        </select>
      </label>
    </div>
  )
}

function StepPreferences({ form, setForm }) {
  return (
    <div className="setup-step">
      <div className="setup-icon-wrapper">
        <Palette size={40} className="text-primary" />
      </div>
      <h2>Editor Preferences</h2>
      <p>Tune the IDE to your liking. You can always change these later.</p>

      <label className="setup-field">
        <span>UI Theme</span>
        <div className="setup-theme-grid">
          <button
            type="button"
            className={`setup-theme-card ${form.theme === 'dark' ? 'active' : ''}`}
            onClick={() => setForm(prev => ({...prev, theme: 'dark'}))}
          >
            <div className="theme-preview dark"></div>
            <span>Arkitect Dark</span>
          </button>
          <button
            type="button"
            className={`setup-theme-card ${form.theme === 'light' ? 'active' : ''}`}
            onClick={() => setForm(prev => ({...prev, theme: 'light'}))}
          >
            <div className="theme-preview light"></div>
            <span>Arkitect Light</span>
          </button>
        </div>
      </label>

      <label className="setup-field mt-4">
        <span>Default AI Agent</span>
        <div className="setup-agent-pills">
          {AI_AGENTS.map(agent => (
            <button
              key={agent.id}
              type="button"
              className={`setup-agent-pill ${form.aiAgent === agent.id ? 'active' : ''}`}
              onClick={() => setForm(prev => ({ ...prev, aiAgent: agent.id }))}
            >
              <Bot size={16} />
              {agent.label}
            </button>
          ))}
        </div>
      </label>
    </div>
  )
}

export function SetupPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [form, setForm] = useState({
    workspaceDir: '',
    visibility: 'private',
    theme: 'dark',
    aiAgent: 'claude',
  })

  const canMoveNext = step === 1 || (step === 2 && form.workspaceDir)

  const handleNext = () => setStep(s => Math.min(3, s + 1))
  const handleBack = () => setStep(s => Math.max(1, s - 1))

  const handleComplete = () => {
    localStorage.setItem('arkitect_setup_completed', 'true')
    localStorage.setItem('arkitect_workspace_dir', form.workspaceDir)
    localStorage.setItem('arkitect_theme', form.theme)
    localStorage.setItem('arkitect_ai_agent', form.aiAgent)
    navigate('/ide', { replace: true })
  }

  return (
    <div className="setup-page-wrapper">
      <div className="setup-ambient-bg"></div>

      <div className="setup-container">
        <div className="setup-header">
          <div className="setup-brand">Arkitect Setup</div>
          <Progress step={step} />
        </div>

        <div className="setup-body">
          {step === 1 && <StepWelcome />}
          {step === 2 && <StepWorkspace form={form} setForm={setForm} />}
          {step === 3 && <StepPreferences form={form} setForm={setForm} />}
        </div>

        <div className="setup-footer">
          {step > 1 ? (
            <button className="setup-btn-ghost" onClick={handleBack}>
              <ArrowLeft size={16} /> Back
            </button>
          ) : <div></div>}

          {step < 3 ? (
            <button
              className="setup-btn-primary"
              onClick={handleNext}
              disabled={!canMoveNext}
            >
              Continue <ArrowRight size={16} />
            </button>
          ) : (
            <button
              className="setup-btn-primary"
              onClick={handleComplete}
            >
              Launch Arkitect <CheckCircle2 size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
